import { GridLegacy as Grid, Typography, Button, Link } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { useFormHook } from '@monorepo/react/hook-form-helper';
import { CustomTextField } from '@monorepo/react/material-form-inputs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { LoginProps } from '..';
import { joinPath } from './path.utils';

type ResetPasswordProps = Pick<
  LoginProps,
  | 'requestPasswordResetMutation'
  | 'requestPasswordResetSmsMutation'
  | 'smsBtnLabel'
  | 'pathPrefix'
>;

// eslint-disable-next-line max-lines-per-function
export const RequestPasswordReset: React.FC<ResetPasswordProps> = ({
  requestPasswordResetMutation,
  requestPasswordResetSmsMutation,
  smsBtnLabel,
  pathPrefix = '',
}) => {
  const { t } = useTranslation();
  const submitModeRef = React.useRef<'email' | 'sms'>('email');
  const {
    register,
    handleSubmit,
    errors,
    formState: { isSubmitSuccessful, isDirty, isSubmitting },
  } = useFormHook({
    defaultValues: { email: '' },
    onSubmit: async (formData) => {
      const submit =
        submitModeRef.current === 'sms' && requestPasswordResetSmsMutation
          ? requestPasswordResetSmsMutation
          : requestPasswordResetMutation;

      const result = await submit({
        variables: formData,
      });
      if (result.errors) return { errors: result.errors };
      return {};
    },
  });

  return (
    <>
      {isSubmitSuccessful && (
        <Alert severity="success" sx={{ mb: 6 }}>
          <AlertTitle>
            {String(t('login.passwordResetRequest.success'))}
          </AlertTitle>
          {String(t('login.passwordResetRequest.successDetails'))}{' '}
          <Link component={RouterLink} to={joinPath(pathPrefix)}>
            {String(t('global.navigation.back'))}
          </Link>
        </Alert>
      )}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom color="primary">
          {String(t('login.passwordResetRequest.self'))}
        </Typography>
      </Grid>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTextField
              name="email"
              errors={errors}
              label={String(t('login.email'))}
              sx={{
                '& .MuiInputLabel-root': {
                  color: '#6f747b',
                  fontSize: '0.95rem',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6f747b',
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#eaf2ff',
                  '& fieldset': {
                    borderColor: '#b7bdc6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#98a0aa',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8ea7c6',
                  },
                },
                '& input': {
                  padding: '14px 14px',
                  fontSize: '0.8rem',
                  color: '#1f2a33',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #eaf2ff inset',
                  WebkitTextFillColor: '#1f2a33',
                  caretColor: '#1f2a33',
                },
              }}
              {...register('email', { required: true })}
            />
          </Grid>
          <Grid item xs={12} sm={smsBtnLabel ? 8 : 12}>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isDirty}
              onClick={() => {
                submitModeRef.current = 'email';
              }}
            >
              {String(t('login.passwordResetRequest.submitButton'))}
            </Button>
          </Grid>
          {smsBtnLabel && requestPasswordResetSmsMutation ? (
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isSubmitting || !isDirty}
                onClick={() => {
                  submitModeRef.current = 'sms';
                }}
              >
                {smsBtnLabel}
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </form>
    </>
  );
};
